import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import NorthStarBanner from "@/components/ascend/NorthStarBanner";
import DreamEditModal from "@/components/ascend/DreamEditModal";
import ThemedQuoteNudge from "@/components/ascend/ThemedQuoteNudge";
import SubjectCard from "@/components/ascend/SubjectCard";
import WeekStrip from "@/components/ascend/WeekStrip";
import AffirmationCard from "@/components/ascend/AffirmationCard";
import ReadWeekendTile from "@/components/ascend/ReadWeekendTile";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { CalendarClock, Sparkles, X, BookHeart, ArrowRight } from "lucide-react";
import { isoDate, weekStartOf, todayDayOfWeek } from "@/lib/time";
import { textColorForSubject } from "@/lib/subjectColor";

const MOOD_EMOJI = { 1: "😔", 2: "😐", 3: "🙂", 4: "😊", 5: "🌟" };

export default function Dashboard() {
  const { user } = useAuth();
  const [dream, setDream] = useState({ full_text: "", north_star_short: "", target_uni: "", target_grades: "" });
  const [dreamModalOpen, setDreamModalOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [todayBlocks, setTodayBlocks] = useState([]);
  const [checkinToday, setCheckinToday] = useState(null);
  const [affirmationPreview, setAffirmationPreview] = useState(null);
  const [reflection, setReflection] = useState(null); // { reflection, auto_stats }
  const [loading, setLoading] = useState(true);
  const [dismissedPill, setDismissedPill] = useState(false);

  const weekStartStr = useMemo(() => isoDate(weekStartOf(new Date())), []);
  const todayDow = useMemo(() => todayDayOfWeek(), []);
  const isSunday = todayDow === 6;

  useEffect(() => {
    (async () => {
      try {
        const [d, s, st, tt, ci, ref] = await Promise.all([
          api.get("/me/dream"),
          api.get("/subjects"),
          api.get(`/stats/week?week_start=${weekStartStr}`),
          api.get(`/timetable?week_start=${weekStartStr}`),
          api.get("/checkins/today"),
          api.get(`/weekly-reflections/${weekStartStr}`),
        ]);
        setDream(d.data || {});
        setSubjects((s.data && s.data.items) || []);
        setStats(st.data);
        const todays = (tt.data?.items || [])
          .filter((b) => b.day_of_week === todayDow)
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
        setTodayBlocks(todays);
        setCheckinToday(ci.data?.checkin || null);
        setReflection(ref.data);
        if (!ci.data?.checkin) {
          // Preview an affirmation to sit in Today tile even before check-in
          try {
            const { data } = await api.get("/affirmation/today");
            setAffirmationPreview(data);
          } catch {}
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [weekStartStr, todayDow]);

  const subjectById = useMemo(() => {
    const m = new Map();
    subjects.forEach((s) => m.set(s.id, s));
    return m;
  }, [subjects]);

  const minutesForSubject = (subjectId) => stats?.subject_minutes?.[subjectId] || 0;

  const firstName = user?.email?.split("@")[0] || "friend";

  return (
    <AppShell>
      <div className="pt-2">
        <div className="flex items-center gap-2 mb-3 fade-up">
          <span className="eyebrow">welcome back,</span>
          <span className="font-hand text-2xl" style={{ color: "var(--primary)" }}>{firstName}</span>
        </div>

        {/* Check-in gentle prompt */}
        {!loading && !checkinToday && !dismissedPill && (
          <CheckinPill onDismiss={() => setDismissedPill(true)} />
        )}

        <NorthStarBanner
          northStar={dream.north_star_short}
          targetUni={dream.target_uni}
          targetGrades={dream.target_grades}
          onEdit={() => setDreamModalOpen(true)}
        />

        <DreamEditModal
          open={dreamModalOpen}
          onClose={() => setDreamModalOpen(false)}
          initial={dream}
          onSaved={(updated) => setDream(updated)}
        />

        <ThemedQuoteNudge />

        <WeekStrip stats={stats} />

        {/* Sunday reflection prompt (prominent) */}
        {!loading && isSunday && !reflection?.reflection && (
          <SundayReflectionPrompt />
        )}

        <div className="mt-2 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: subjects */}
          <section className="lg:col-span-8" data-testid="subjects-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-2xl sm:text-3xl" style={{ color: "var(--text)" }}>Your subjects</h2>
              <span className="font-hand text-base" style={{ color: "var(--accent)" }}>this week</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (<div key={i} className="card-soft p-6 h-40 animate-pulse" />))
                : subjects.map((s, i) => <SubjectCard key={s.id} subject={s} index={i} minutesLogged={minutesForSubject(s.id)} />)}
            </div>
          </section>

          {/* Right: Today + reflection teaser */}
          <aside className="lg:col-span-4 space-y-5">
            {/* Today tile — mood + affirmation */}
            <div className="card-soft p-6 fade-up delay-1" data-testid="today-tile">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
                <span className="eyebrow">today</span>
              </div>
              {checkinToday ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-4xl">{MOOD_EMOJI[checkinToday.mood]}</span>
                    <div>
                      <div className="font-display text-lg leading-tight" style={{ color: "var(--text)" }}>
                        {describeMood(checkinToday.mood)}
                      </div>
                      <Link to="/checkin" className="text-xs" style={{ color: "var(--primary)" }} data-testid="today-edit-checkin">
                        edit today
                      </Link>
                    </div>
                  </div>
                  {checkinToday.affirmation_shown && (
                    <AffirmationCard
                      text={checkinToday.affirmation_shown}
                      source={checkinToday.affirmation_source}
                      size="sm"
                      testid="today-affirmation"
                    />
                  )}
                </>
              ) : (
                <>
                  {affirmationPreview?.text && (
                    <AffirmationCard
                      text={affirmationPreview.text}
                      source={affirmationPreview.source}
                      size="sm"
                      testid="today-affirmation-preview"
                    />
                  )}
                  <div className="mt-4">
                    <Link to="/checkin" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="today-checkin-cta">
                      Check in <ArrowRight size={14} strokeWidth={1.75} />
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Today blocks */}
            <div className="card-soft p-6 fade-up delay-2" data-testid="today-section">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
                <span className="eyebrow">today's blocks</span>
              </div>
              {todayBlocks.length === 0 ? (
                <div>
                  <p className="text-sm text-muted mb-3">Nothing scheduled today.</p>
                  <Link to="/timetable" className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="today-plan-cta">
                    Plan your week <ArrowRight size={14} strokeWidth={1.75} />
                  </Link>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="today-blocks">
                  {todayBlocks.map((b) => {
                    const s = subjectById.get(String(b.subject_id));
                    const color = s?.color || "#888";
                    const nameColor = textColorForSubject(color);
                    return (
                      <li
                        key={b.id}
                        className="relative flex items-center gap-2 pl-4 pr-3 py-2 rounded-xl overflow-hidden"
                        style={{
                          background: `color-mix(in oklab, ${color} 16%, var(--surface-2))`,
                          border: `1px solid color-mix(in oklab, ${color} 30%, var(--border))`,
                        }}
                        data-testid={`today-block-${b.start_time.replace(":","-")}`}
                      >
                        <span
                          aria-hidden
                          className="absolute left-0 top-0 bottom-0"
                          style={{ width: 4, background: color }}
                        />
                        <span className="text-sm font-medium tabular-nums" style={{ color: "var(--text)" }}>{b.start_time}</span>
                        <span className="text-sm text-muted">·</span>
                        <span className="text-sm truncate font-medium" style={{ color: nameColor }}>{s?.name || "Study"}</span>
                        <span
                          className="ml-auto text-[10px] px-2 py-0.5 rounded-full"
                          style={{
                            background: b.status === "completed" ? color : "var(--surface)",
                            color: b.status === "completed" ? "#fff" : "var(--text-muted)",
                            border: "1px solid var(--border)",
                          }}
                        >
                          {b.status}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Reflection teaser */}
            <div
              className="card-soft p-6 fade-up delay-3"
              data-testid="reflection-tile"
              style={isSunday ? {
                background: "linear-gradient(135deg, var(--grad-a), var(--grad-b))",
                border: "1px solid var(--border)",
              } : {}}
            >
              <div className="flex items-center gap-2 mb-2">
                <BookHeart size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
                <span className="eyebrow">{isSunday ? "it's Sunday" : "this week"}</span>
              </div>
              <h3 className="font-display text-2xl leading-tight" style={{ color: "var(--text)" }}>
                {reflection?.reflection
                  ? "Your reflection is saved"
                  : isSunday
                    ? "Want to look back?"
                    : "Weekly reflection"}
              </h3>
              <p className="text-sm text-muted mt-2">
                {reflection?.reflection
                  ? "Peek in and edit any time."
                  : "A gentle look at how the week actually felt."}
              </p>
              <Link to="/reflection" className={`${isSunday || reflection?.reflection ? "btn-primary" : "btn-ghost"} inline-flex items-center gap-2 mt-3 text-sm`} data-testid="reflection-open-btn">
                {reflection?.reflection ? "Open" : "Reflect"} <ArrowRight size={14} strokeWidth={1.75} />
              </Link>
            </div>

            {/* Share your week */}
            <div className="card-soft p-6 fade-up delay-4" data-testid="share-tile">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
                <span className="eyebrow">for someone rooting for you</span>
              </div>
              <h3 className="font-display text-2xl leading-tight" style={{ color: "var(--text)" }}>Share your week</h3>
              <p className="text-sm text-muted mt-2">
                You choose exactly what they see. Private notes never leave you.
              </p>
              <Link to="/share" className="btn-ghost inline-flex items-center gap-2 mt-3 text-sm" data-testid="share-open-btn">
                open share <ArrowRight size={14} strokeWidth={1.75} />
              </Link>
            </div>

            {/* Read Weekend — only surfaces once she has visited /news */}
            <ReadWeekendTile />
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

function describeMood(m) {
  return { 1: "heavy day", 2: "meh day", 3: "okay day", 4: "good day", 5: "shining day" }[m] || "";
}

function CheckinPill({ onDismiss }) {
  return (
    <div
      className="rounded-full px-4 py-2.5 mb-4 flex items-center gap-3 fade-up"
      style={{
        background: "color-mix(in oklab, var(--primary-soft) 55%, var(--surface))",
        border: "1px solid var(--border)",
      }}
      data-testid="checkin-pill"
    >
      <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
      <span className="text-sm" style={{ color: "var(--text)" }}>check in with yourself?</span>
      <Link to="/checkin" className="text-sm font-semibold" style={{ color: "var(--primary)" }} data-testid="checkin-pill-cta">
        one minute →
      </Link>
      <button
        onClick={onDismiss}
        className="ml-auto p-1 rounded-full hover:bg-[var(--surface)]"
        data-testid="checkin-pill-dismiss"
        aria-label="Dismiss"
      >
        <X size={12} strokeWidth={1.5} />
      </button>
    </div>
  );
}

function SundayReflectionPrompt() {
  return (
    <div
      className="rounded-3xl p-6 sm:p-7 mb-6 fade-up"
      style={{
        background: "linear-gradient(135deg, var(--grad-a), var(--grad-b) 55%, var(--grad-c))",
        border: "1px solid var(--border)",
      }}
      data-testid="sunday-reflection-prompt"
    >
      <div className="flex items-center gap-2 mb-1">
        <BookHeart size={16} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
        <span className="eyebrow">it's Sunday</span>
      </div>
      <h3 className="font-display text-2xl sm:text-3xl leading-tight mb-2" style={{ color: "var(--text)" }}>
        Want to look back on this week?
      </h3>
      <p className="text-sm text-muted mb-4 max-w-xl">
        A few gentle prompts. Ten minutes, no pressure. Your future-Monday self will thank you.
      </p>
      <Link to="/reflection" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="sunday-open-reflection">
        Open reflection <ArrowRight size={14} strokeWidth={1.75} />
      </Link>
    </div>
  );
}
